
import axios from 'axios';
import { fun } from '@/lib/request';
import { api } from '@/lib/env';
import { StateSlice } from '@/stores/appStore';
import { formatData, snakeToCamel, transferSnakeToCamel } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import { PaperId } from './paperSlice';
import { SubjectId } from './subjectSlice';
import { WrapResponse } from '@/types';
import { NoteData, Note, Notes, NoteId, NoteType } from '@/stores/context/createNoteStore';
import { getFileHash } from '@/lib/getFileHash';

type Id = SubjectId | PaperId;
// 服务端返回的数据
type NotesData = {
  paperId: PaperId;
  paperName: string;
  title: string;
  rootId: string;
  notes: NoteData[];
};
type NoteEntity = {
  subjectId: SubjectId;
  paperId: PaperId;
  paperName: string;
  title: string;
} & Notes;

type FileInfo = {
  id: string;
  md5: string;
  sha1: string;
};

export type MindSlice = {
  noteEntities: Record<Id, NoteEntity>,
  allPaperIds: PaperId[],
  fetchNoteLoading: boolean;
  fetchNotes: (id: Id, isSubject?: boolean) => Promise<void>;
  fetchAllNotes: (subjectId?: string) => Promise<void>;
  uploadImages: (files: Blob[], scene?: string) => Promise<string[]>;
};

export const createMindSlice: StateSlice<MindSlice> = (set, get) => {
  return ({
    noteEntities: {},
    allPaperIds: [],
    fetchNoteLoading: false,
    fetchNotes: async (id, isSubject = false) => {
      set((state) => {
        state.fetchNoteLoading = true;
      });
      try {
        const formData = new FormData();
        isSubject ? (
          formData.append('subject_id', id)
        ) : (
          formData.append('paper_id', id)
        );
        const { data, code, message: msg } = (
          await fun.post<WrapResponse<NotesData>>(
            api('/note/list', true),
            formData,
          )
        ).data;
        if (code === 0) {
          const { rootId, notes, paperName, title } = snakeToCamel<NotesData>(data);
          set((state) => {
            const entities: Record<NoteId, Note> = {};
            notes.forEach((data) => {
              const { noteId, pid, paperId, children = [], viewer, images, type, ...rest } = data;
              if (noteId) {
                entities[noteId] = {
                  viewer: JSON.parse(viewer || '{}'),
                  images: JSON.parse(images || '[]'),
                  type: transferSnakeToCamel(type) as NoteType,
                  ...rest, // content, ...
                  pid: pid.toString(),
                  id: noteId.toString(),
                  children: (children || [])?.map((cid) => cid.toString()),
                };
              }
            });
            state.noteEntities[id] = {
              entities,
              rootId: rootId.toString(),
              paperName,
              title,
              paperId: isSubject ? '' : id,
              subjectId: isSubject ? id : '',
            };
          });
          return;
        } else {
          toast.error(msg);
          console.error('Error fetch notes:', msg);
        }
      } catch (error) {
        console.error('Error fetch notes:', error);
      } finally {
        set((state) => {
          state.fetchNoteLoading = false;
        });
      }
      return;
    },
    fetchAllNotes: async (subjectId = '') => {
      set((state) => {
        state.fetchNoteLoading = true;
      });
      try {
        const formData = new FormData();
        subjectId && formData.append('subject_id', subjectId);
        const { data, code, message: msg } = (
          await fun.post<WrapResponse<any>>(
            api('/note/listAll', true),
            formData,
          )
        ).data;
        if (code === 0) {
          const allNotesData = formatData<NotesData[]>(data);
          const allNotes: PaperId[] = [];
          set((state) => {
            allNotesData.forEach(({ notes, paperId, paperName, title, rootId }) => {
              const entities: Record<NoteId, Note> = {};
              notes.forEach((data) => {
                const { noteId, pid, paperId, children = [], viewer, images, ...rest } = data;
                if (noteId) {
                  entities[noteId] = {
                    viewer: JSON.parse(viewer || '{}'),
                    images: JSON.parse(images || '[]'),
                    ...rest, // type, content, ...
                    pid: pid?.toString() || entities[noteId].pid,
                    id: noteId?.toString(),
                    children: (children || []).map((cid) => cid?.toString()),
                  };
                }
              });
              state.noteEntities[paperId] = {
                entities,
                rootId: rootId?.toString(),
                paperName,
                title,
                paperId: paperId.toString(),
                subjectId: '',
              }
              allNotes.push(paperId);
            });
            state.allPaperIds = allNotes;
          });
          return;
        } else {
          toast.error(msg);
        }
        console.error('Error fetch notes:', msg);
      } catch (error) {
        console.error('Error fetch notes:', error);
      } finally {
        set((state) => {
          state.fetchNoteLoading = false;
        });
      }
      return;
    },
    uploadImages: async (files, scene = 'note_img') => {
      const TOAST_ID = 'uploading'
      toast.loading('截图上传中', {
        id: TOAST_ID,
      });
      try {
        const filesInfo: FileInfo[] = [];
        for (const file of files) {
          const md5 = await getFileHash(file, 'MD5');
          const sha1 = await getFileHash(file, 'SHA1');
          const id = `${md5}${sha1}`;
          filesInfo.push({
            id,
            md5,
            sha1,
          });
        }
        const uploadFormData = new FormData();
        filesInfo.forEach((data, i) => {
          uploadFormData.append('scene', scene);
          uploadFormData.append(`files[${i}][md5]`, data.md5);
          uploadFormData.append(`files[${i}][sha1]`, data.sha1);
          uploadFormData.append(`files[${i}][content-type]`, 'image/png');
        })
        // 获取到可上传的到 oss 的签名 url
        const uploadData = (await fun.post<WrapResponse<string[]>>(
          api('/upload/getUploadUrl', true), uploadFormData
        )).data;
        // 检测
        if (uploadData.code === 0) {
          const uploads = uploadData.data || [];
          for (let index = 0; index < uploads.length; index++) {
            const uploadUrl = uploads[index];
            try {
              await axios.put<WrapResponse<{}>>(
                uploadUrl,
                files[index],
                {
                  headers: {
                    'Content-Type': 'image/png',
                  },
                }
              );
            } catch (error) {
              console.error('Error uploading file:', error);
            }
          }
          return uploads.map(url => url.split('?')[0]);
        } else {
          toast.error(uploadData.message);
          return [];
        }
      } catch (error) {
        console.error('Error get url file:', error);
        return [];
      } finally {
        toast.dismiss(TOAST_ID);
      }
    },
  });
}